/**
 * @fileoverview Editable list of contact addresses.
 *
 * Used by both the Job Contact detail panel and the Firm Contact detail
 * panel. Each address row exposes structured fields (line1, line2,
 * suburb, state, postcode, country) plus a free-text `sourceLabel` that
 * captures things like "Registered office" or "Postal".
 *
 * State management is intentionally lifted out: parents own the array of
 * addresses and pass `onChange`. Autosave is triggered via `onCommit` —
 * fired on row blur and on add/remove — so callers can flush a debounced
 * autosave hook without round-tripping every keystroke.
 */

import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { AU_STATES } from "@/utils/format/auStates";
import { createEmptyAddress } from "./types";
import type { ContactAddress } from "./types";

const DEFAULT_COUNTRY = "Australia";
const NONE_STATE = "__none__";

interface AddressesEditorProps {
  addresses: Array<ContactAddress>;
  onChange: (next: Array<ContactAddress>) => void;
  /** Called after a value commits (blur, state select change, add/remove). */
  onCommit?: () => void;
  disabled?: boolean;
  /**
   * When addresses already exist, show an "Add address" button under the
   * list. The job contact panel turns this off and puts that action in the
   * section heading instead.
   */
  showTrailingAdd?: boolean;
}

export function AddressesEditor({
  addresses,
  onChange,
  onCommit,
  disabled,
  showTrailingAdd = true,
}: AddressesEditorProps) {
  const update = (index: number, patch: Partial<ContactAddress>) => {
    const next = addresses.map((a, i) =>
      i === index ? { ...a, ...patch } : a,
    );
    onChange(next);
  };

  const remove = (index: number) => {
    const next = addresses.filter((_, i) => i !== index);
    onChange(next);
    onCommit?.();
  };

  const add = () => {
    onChange([...addresses, createEmptyAddress()]);
    onCommit?.();
  };

  const addButton = (
    <Button
      variant="outline"
      size="sm"
      className="shrink-0 gap-2"
      disabled={disabled}
      onClick={add}
      type="button"
    >
      <Plus className="h-3.5 w-3.5" />
      Add address
    </Button>
  );

  return (
    <div className="flex flex-col gap-3">
      {addresses.length === 0 ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/10 p-3">
          <p className="text-xs text-muted-foreground">
            No addresses yet. Add one to capture a registered office, postal
            address, or site location.
          </p>
          {addButton}
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {addresses.map((addr, i) => (
            <li
              key={i}
              className="flex flex-col gap-2 rounded-lg border border-border bg-muted/10 p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <Input
                  aria-label="Address label"
                  value={addr.sourceLabel}
                  placeholder="Label (e.g. Registered office, Postal)"
                  disabled={disabled}
                  onChange={(e) => update(i, { sourceLabel: e.target.value })}
                  onBlur={() => onCommit?.()}
                  className="h-8 text-xs font-medium"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                  disabled={disabled}
                  onClick={() => remove(i)}
                  aria-label="Remove address"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>

              <AddressFieldStack
                address={addr}
                disabled={disabled}
                onChange={(patch) => update(i, patch)}
                onCommit={() => onCommit?.()}
              />
            </li>
          ))}
        </ul>
      )}

      {showTrailingAdd && addresses.length > 0 && <div>{addButton}</div>}
    </div>
  );
}

function AddressFieldStack({
  address,
  disabled,
  onChange,
  onCommit,
}: {
  address: ContactAddress;
  disabled?: boolean;
  onChange: (patch: Partial<ContactAddress>) => void;
  onCommit: () => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-6">
      <FieldCell label="Line 1" className="sm:col-span-3">
        <Input
          value={address.line1 ?? ""}
          placeholder="Street address"
          disabled={disabled}
          onChange={(e) => onChange({ line1: e.target.value })}
          onBlur={onCommit}
        />
      </FieldCell>
      <FieldCell label="Line 2" className="sm:col-span-3">
        <Input
          value={address.line2 ?? ""}
          placeholder="Unit, suite, building (optional)"
          disabled={disabled}
          onChange={(e) => onChange({ line2: e.target.value })}
          onBlur={onCommit}
        />
      </FieldCell>
      <FieldCell label="Suburb" className="sm:col-span-3">
        <Input
          value={address.suburb ?? ""}
          disabled={disabled}
          onChange={(e) => onChange({ suburb: e.target.value })}
          onBlur={onCommit}
        />
      </FieldCell>
      <FieldCell label="State" className="sm:col-span-1">
        <Select
          value={address.state || NONE_STATE}
          disabled={disabled}
          onValueChange={(v) => {
            onChange({ state: v === NONE_STATE ? "" : v });
            setTimeout(onCommit, 0);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_STATE}>—</SelectItem>
            {AU_STATES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldCell>
      <FieldCell label="Postcode" className="sm:col-span-1">
        <Input
          value={address.postcode ?? ""}
          inputMode="numeric"
          disabled={disabled}
          onChange={(e) => onChange({ postcode: e.target.value })}
          onBlur={onCommit}
          className="font-mono"
        />
      </FieldCell>
      <FieldCell label="Country" className="sm:col-span-1">
        <Input
          value={address.country ?? ""}
          placeholder={DEFAULT_COUNTRY}
          disabled={disabled}
          onChange={(e) => onChange({ country: e.target.value })}
          onBlur={onCommit}
        />
      </FieldCell>
    </div>
  );
}

function FieldCell({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={"flex flex-col gap-1 " + (className ?? "")}>
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
        {label}
      </Label>
      {children}
    </div>
  );
}
